import React from "react";
import { Badge, Button, Callout, Card, Checkbox, DataTable, Dialog, DisabledReason, EmptyState, Field, IconButton, Input, PageHeader, ProgressBar, Select, Stat, Tabs, Textarea } from "../components/DesignSystem";
import type {
  BudgetCategory,
  BudgetExpenseInput,
  ID,
  Payment,
  PlannerTask,
  Scope,
  TaskInput,
  TaskPriority,
  TaskStatus,
  Vendor,
  VendorInput,
  VendorStatus,
  Venue,
  VenueInput,
  VenueStatus
} from "../domain/types";
import { eventName } from "../services/derived";
import { errorMessage } from "../services/errors";
import type { RouteKey } from "../shell/PlannerShell";
import { usePlannerData } from "../state/PlannerDataContext";
import { ErrorState, formatShortDate, LoadingState, useIdempotencyKey, useSubmitGuard } from "./common";

type TaskTab = "all" | "mine" | "overdue" | "week" | "completed";
type VendorTab = "all" | VendorStatus;
type BudgetTab = "overview" | "category" | "event" | "contributor";

const statusLabels: Record<TaskStatus, string> = {
  todo: "To do",
  in_progress: "In progress",
  waiting: "Waiting",
  done: "Done"
};

const statusTone: Record<TaskStatus, "neutral" | "warning" | "success"> = {
  todo: "neutral",
  in_progress: "warning",
  waiting: "warning",
  done: "success"
};

const vendorLabels: Record<VendorStatus, string> = {
  booked: "Booked",
  quoted: "Quoted",
  shortlisted: "Shortlisted",
  contacted: "Contacted",
  researching: "Researching",
  rejected: "Rejected",
  completed: "Completed",
  negotiating: "Negotiating"
};

const vendorTone: Record<VendorStatus, "neutral" | "warning" | "success" | "error"> = {
  booked: "success",
  quoted: "warning",
  shortlisted: "neutral",
  contacted: "neutral",
  researching: "neutral",
  rejected: "error",
  completed: "success",
  negotiating: "warning"
};

const venueLabels: Record<VenueStatus, string> = {
  booked: "Booked",
  confirmed: "Confirmed",
  shortlisted: "Shortlisted",
  researching: "Researching"
};

const paymentLabels: Record<Payment["status"], string> = {
  due: "Due",
  upcoming: "Upcoming",
  not_due: "Not due",
  paid: "Paid",
  overdue: "Overdue"
};

const paymentTone: Record<Payment["status"], "neutral" | "warning" | "success" | "error"> = {
  due: "error",
  upcoming: "warning",
  not_due: "neutral",
  paid: "success",
  overdue: "error"
};

function money(value: number, currency = "PKR"): string {
  return `${currency} ${new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 }).format(value || 0)}`;
}

function shortMoney(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(value % 1_000_000 === 0 ? 0 : 1)}m`;
  if (abs >= 1_000) return `${Math.round(value / 1_000)}k`;
  return String(value || 0);
}

function scopedRecord(record: { eventId?: ID | "shared" }, scope: Scope): boolean {
  if (scope === "all") return true;
  if (scope === "shared") return record.eventId === "shared" || !record.eventId;
  return record.eventId === scope || record.eventId === "shared" || !record.eventId;
}

function scopedMultiRecord(record: { eventIds: Array<ID | "shared"> }, scope: Scope): boolean {
  if (scope === "all") return true;
  if (scope === "shared") return record.eventIds.includes("shared");
  return record.eventIds.includes(scope) || record.eventIds.includes("shared");
}

function taskDueSoon(task: PlannerTask): boolean {
  if (!task.dueDate) return false;
  const due = new Date(`${task.dueDate}T12:00:00`).getTime();
  const weddingWindow = new Date("2027-08-01T12:00:00").getTime();
  return due >= weddingWindow && due <= weddingWindow + 7 * 24 * 60 * 60 * 1000;
}

function taskOverdue(task: PlannerTask): boolean {
  if (!task.dueDate || task.status === "done") return false;
  return new Date(`${task.dueDate}T12:00:00`).getTime() < new Date("2027-08-01T12:00:00").getTime();
}

function firstName(name?: string): string {
  return (name || "Fatima").split(/\s+/)[0] || "Fatima";
}

export function TasksScreen({ scope }: { scope: Scope; go: (route: RouteKey) => void }) {
  const { workspace, session, loading, error, service, run } = usePlannerData();
  const [tab, setTab] = React.useState<TaskTab>("all");
  const [drawer, setDrawer] = React.useState<PlannerTask | null>(null);
  const [adding, setAdding] = React.useState(false);
  const [addingAttempt, setAddingAttempt] = React.useState(0);
  const [showSuggestions, setShowSuggestions] = React.useState(true);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const { submitting: addingTask, guardedSubmit: guardedAddTask } = useSubmitGuard();
  const idempotencyKey = useIdempotencyKey(addingAttempt);
  if (loading || !workspace) return <div className="pob-pad"><LoadingState label="Loading tasks" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;

  const mine = firstName(session?.user.name);
  const all = workspace.tasks.filter((task) => scopedRecord(task, scope));
  const rows = all.filter((task) => {
    if (tab !== "completed" && task.status === "done") return false;
    if (tab === "mine") return task.owner === mine;
    if (tab === "overdue") return taskOverdue(task);
    if (tab === "week") return taskDueSoon(task);
    if (tab === "completed") return task.status === "done";
    return true;
  });

  const setStatus = async (task: PlannerTask, status: TaskStatus) => {
    setActionError(null);
    try {
      await run(() => service.tasks.setTaskStatus(task.id, status));
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };

  const addSuggestion = async (suggestionId: ID) => {
    setActionError(null);
    try {
      await run(() => service.tasks.addSuggestion(suggestionId));
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };

  const dismissSuggestion = async (suggestionId: ID) => {
    setActionError(null);
    try {
      await run(() => service.tasks.dismissSuggestion(suggestionId));
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow={scope === "all" ? "Plan" : "Event scope"}
        title="Tasks"
        meta={[
          { icon: "square-check", label: `${all.filter((task) => task.status !== "done").length} open` },
          { icon: "triangle-alert", label: `${all.filter(taskOverdue).length} overdue` },
          { icon: "sparkle", label: `${workspace.taskSuggestions.length} suggested` }
        ]}
        actions={<><Button icon="list" onClick={() => setShowSuggestions(!showSuggestions)}>Suggestions</Button><Button variant="primary" icon="plus" onClick={() => { setAddingAttempt((n) => n + 1); setAdding(true); }}>Add task</Button></>}
        tabs={<Tabs value={tab} onChange={setTab} tabs={[
          { value: "all", label: "All", count: all.filter((task) => task.status !== "done").length },
          { value: "mine", label: "Mine", count: all.filter((task) => task.owner === mine).length },
          { value: "overdue", label: "Overdue", count: all.filter(taskOverdue).length },
          { value: "week", label: "This week", count: all.filter(taskDueSoon).length },
          { value: "completed", label: "Completed", count: all.filter((task) => task.status === "done").length }
        ]} />}
      />
      <div className="pob-pad">
        {actionError && <Callout tone="warning" title="Task action not saved">{actionError}</Callout>}
        {tab === "overdue" && rows.length > 0 && (
          <Callout tone="error" title={`${rows.length} tasks are past their deadline`}>
            The oldest is {rows[0].title}, owned by {rows[0].owner}, due {formatShortDate(rows[0].dueDate || "")}.
          </Callout>
        )}
        <Card padding="0">
          <DataTable
            rows={rows}
            onRowClick={setDrawer}
            emptyLabel="Nothing in this view"
            columns={[
              { key: "check", header: "", width: 42, render: (task) => <span onClick={(event) => event.stopPropagation()}><Checkbox checked={task.status === "done"} onChange={() => setStatus(task, task.status === "done" ? "todo" : "done")} /></span> },
              { key: "title", header: "Task", render: (task) => <span style={{ font: "var(--type-label)", textDecoration: task.status === "done" ? "line-through" : "none" }}>{task.title}</span> },
              { key: "event", header: "Celebration", muted: true, render: (task) => task.eventId === "shared" || !task.eventId ? "Shared" : eventName(workspace, task.eventId) },
              { key: "category", header: "Category", muted: true },
              { key: "owner", header: "Owner" },
              { key: "dueDate", header: "Due", render: (task) => <span style={{ color: taskOverdue(task) ? "var(--error-fg)" : "inherit" }}>{task.dueDate ? formatShortDate(task.dueDate) : "-"}</span> },
              { key: "status", header: "Status", render: (task) => <Badge dot tone={statusTone[task.status]}>{statusLabels[task.status]}</Badge> },
              { key: "vendorName", header: "Vendor", muted: true, render: (task) => task.vendorName || "-" }
            ]}
          />
        </Card>
        {showSuggestions && (
          <Card eyebrow="Suggested" title="Not yet added" padding="0">
            <DataTable
              dense
              rows={workspace.taskSuggestions.filter((suggestion) => scopedRecord(suggestion, scope))}
              emptyLabel="All suggestions dismissed."
              columns={[
                { key: "title", header: "Suggestion", render: (suggestion) => <span style={{ font: "var(--type-label)" }}>{suggestion.title}</span> },
                { key: "reason", header: "Why", muted: true, wrap: true },
                { key: "actions", header: "", width: 170, render: (suggestion) => <span style={{ display: "flex", gap: "var(--space-2)" }}><Button size="sm" onClick={() => addSuggestion(suggestion.id)}>Add</Button><Button size="sm" variant="ghost" onClick={() => dismissSuggestion(suggestion.id)}>Dismiss</Button></span> }
              ]}
            />
          </Card>
        )}
      </div>
      {adding && <TaskDialog submitting={addingTask} onClose={() => setAdding(false)} onSubmit={(input) => guardedAddTask(async () => {
        setActionError(null);
        try {
          await run(() => service.tasks.createTask(input, idempotencyKey));
          setAdding(false);
        } catch (err) {
          setActionError(errorMessage(err));
        }
      })} />}
      {drawer && <TaskDrawer task={drawer} onClose={() => setDrawer(null)} />}
    </div>
  );
}

function TaskDialog({ onClose, onSubmit, submitting }: { onClose: () => void; onSubmit: (input: TaskInput) => void; submitting?: boolean }) {
  const { workspace, session } = usePlannerData();
  const [draft, setDraft] = React.useState<TaskInput>({ title: "", eventId: "shared", category: "Planning", owner: firstName(session?.user.name), dueDate: "", status: "todo", priority: "normal" });
  if (!workspace) return null;
  const valid = Boolean(draft.title.trim() && draft.category.trim() && draft.owner.trim());
  return (
    <Dialog title="Add task" description="Tasks can belong to one celebration or the whole wedding." onClose={onClose} footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" disabled={!valid || submitting} onClick={() => onSubmit({ ...draft, dueDate: draft.dueDate || undefined })}>{submitting ? "Adding…" : "Add task"}</Button></>}>
      <TaskForm draft={draft} setDraft={setDraft} />
    </Dialog>
  );
}

function TaskDrawer({ task, onClose }: { task: PlannerTask; onClose: () => void }) {
  const { workspace, service, run } = usePlannerData();
  const current = workspace?.tasks.find((item) => item.id === task.id) || task;
  const [draft, setDraft] = React.useState<TaskInput>({ title: current.title, eventId: current.eventId, category: current.category, owner: current.owner, dueDate: current.dueDate || "", status: current.status, priority: current.priority, vendorId: current.vendorId, detail: current.detail || "" });
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [newSubtask, setNewSubtask] = React.useState("");
  const [newComment, setNewComment] = React.useState("");
  const { submitting: saving, guardedSubmit: guardedSave } = useSubmitGuard();
  const { submitting: markingDone, guardedSubmit: guardedMarkDone } = useSubmitGuard();
  const { submitting: deleting, guardedSubmit: guardedDelete } = useSubmitGuard();
  const { submitting: addingSubtask, guardedSubmit: guardedAddSubtask } = useSubmitGuard();
  const { submitting: addingComment, guardedSubmit: guardedAddComment } = useSubmitGuard();
  if (!workspace) return null;
  const save = () => guardedSave(async () => {
    setActionError(null);
    try {
      await run(() => service.tasks.updateTask(current.id, { ...draft, dueDate: draft.dueDate || undefined }));
      onClose();
    } catch (err) {
      setActionError(errorMessage(err));
    }
  });
  const markDone = () => guardedMarkDone(async () => {
    setActionError(null);
    try {
      await run(() => service.tasks.setTaskStatus(current.id, "done"));
      onClose();
    } catch (err) {
      setActionError(errorMessage(err));
    }
  });
  const remove = () => guardedDelete(async () => {
    setActionError(null);
    try {
      await run(() => service.tasks.deleteTask(current.id));
      onClose();
    } catch (err) {
      setActionError(errorMessage(err));
    }
  });
  const addSubtask = () => guardedAddSubtask(async () => {
    if (!newSubtask.trim()) return;
    setActionError(null);
    try {
      await run(() => service.tasks.addSubtask(current.id, newSubtask.trim()));
      setNewSubtask("");
    } catch (err) {
      setActionError(errorMessage(err));
    }
  });
  const toggleSubtask = async (subtaskId: ID, done: boolean) => {
    setActionError(null);
    try {
      await run(() => service.tasks.toggleSubtask(current.id, subtaskId, done));
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };
  const removeSubtask = async (subtaskId: ID) => {
    setActionError(null);
    try {
      await run(() => service.tasks.deleteSubtask(current.id, subtaskId));
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };
  const addComment = () => guardedAddComment(async () => {
    if (!newComment.trim()) return;
    setActionError(null);
    try {
      await run(() => service.tasks.addComment(current.id, newComment.trim()));
      setNewComment("");
    } catch (err) {
      setActionError(errorMessage(err));
    }
  });
  return (
    <Drawer title={current.title} subtitle={`${current.category} · ${current.owner}`} onClose={onClose}>
      {actionError && <Callout tone="warning" title="Task update not saved">{actionError}</Callout>}
      {current.blockedReason && <Callout tone="warning" title="Blocked">{current.blockedReason}</Callout>}
      <TaskForm draft={draft} setDraft={setDraft} />
      <Card eyebrow="Subtasks" padding="var(--space-4)">
        {current.subtasks.length > 0 && (
          <DataTable dense rows={current.subtasks} columns={[
            { key: "done", header: "", width: 36, render: (subtask) => <Checkbox checked={subtask.done} onChange={() => toggleSubtask(subtask.id, !subtask.done)} /> },
            { key: "title", header: "Subtask", render: (subtask) => <span style={{ textDecoration: subtask.done ? "line-through" : "none" }}>{subtask.title}</span> },
            { key: "dueDate", header: "Due", muted: true, render: (subtask) => subtask.dueDate ? formatShortDate(subtask.dueDate) : "-" },
            { key: "actions", header: "", width: 70, render: (subtask) => <Button size="sm" variant="ghost" onClick={() => removeSubtask(subtask.id)}>Remove</Button> }
          ]} />
        )}
        <div style={{ display: "flex", gap: "var(--space-2)", marginTop: current.subtasks.length > 0 ? "var(--space-3)" : 0 }}>
          <Input value={newSubtask} onChange={(event) => setNewSubtask(event.target.value)} placeholder="Add a subtask" style={{ flex: 1 }} />
          <Button size="sm" disabled={!newSubtask.trim() || addingSubtask} onClick={addSubtask}>{addingSubtask ? "Adding…" : "Add"}</Button>
        </div>
      </Card>
      <Card eyebrow="Comments" padding="var(--space-4)">
        {current.comments.length > 0 && (
          <DataTable dense rows={current.comments} columns={[
            { key: "who", header: "Who", width: 100 },
            { key: "when", header: "When", width: 120, muted: true },
            { key: "text", header: "Note", wrap: true }
          ]} />
        )}
        <div style={{ display: "flex", gap: "var(--space-2)", marginTop: current.comments.length > 0 ? "var(--space-3)" : 0 }}>
          <Input value={newComment} onChange={(event) => setNewComment(event.target.value)} placeholder="Add a comment" style={{ flex: 1 }} />
          <Button size="sm" disabled={!newComment.trim() || addingComment} onClick={addComment}>{addingComment ? "Adding…" : "Add"}</Button>
        </div>
      </Card>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "var(--space-2)" }}>
        <Button variant="danger" disabled={deleting} onClick={remove}>{deleting ? "Deleting…" : "Delete"}</Button>
        <Button onClick={onClose} disabled={saving || markingDone || deleting}>Cancel</Button>
        <Button onClick={markDone} disabled={current.status === "done" || markingDone}>{markingDone ? "Saving…" : "Mark done"}</Button>
        <Button variant="primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</Button>
      </div>
    </Drawer>
  );
}

function TaskForm({ draft, setDraft }: { draft: TaskInput; setDraft: (draft: TaskInput) => void }) {
  const { workspace } = usePlannerData();
  if (!workspace) return null;
  const eventOptions = [{ value: "shared", label: "Shared" }, ...workspace.events.map((event) => ({ value: event.id, label: event.name }))];
  const vendorOptions = [{ value: "", label: "No vendor" }, ...workspace.vendors.map((vendor) => ({ value: vendor.id, label: vendor.name }))];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <Field label="Title"><Input value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} /></Field>
      <div className="pob-grid-2">
        <Field label="Celebration"><Select value={draft.eventId || "shared"} onChange={(event) => setDraft({ ...draft, eventId: event.target.value as ID | "shared" })} options={eventOptions} /></Field>
        <Field label="Category"><Input value={draft.category} onChange={(event) => setDraft({ ...draft, category: event.target.value })} /></Field>
        <Field label="Owner"><Input value={draft.owner} onChange={(event) => setDraft({ ...draft, owner: event.target.value })} /></Field>
        <Field label="Due"><Input type="date" value={draft.dueDate || ""} onChange={(event) => setDraft({ ...draft, dueDate: event.target.value })} /></Field>
        <Field label="Status"><Select value={draft.status || "todo"} onChange={(event) => setDraft({ ...draft, status: event.target.value as TaskStatus })} options={Object.entries(statusLabels).map(([value, label]) => ({ value, label }))} /></Field>
        <Field label="Priority"><Select value={draft.priority || "normal"} onChange={(event) => setDraft({ ...draft, priority: event.target.value as TaskPriority })} options={[{ value: "low", label: "Low" }, { value: "normal", label: "Normal" }, { value: "high", label: "High" }]} /></Field>
      </div>
      <Field label="Vendor"><Select value={draft.vendorId || ""} onChange={(event) => setDraft({ ...draft, vendorId: event.target.value || undefined })} options={vendorOptions} /></Field>
      <Field label="Description"><Textarea value={draft.detail || ""} onChange={(event) => setDraft({ ...draft, detail: event.target.value })} /></Field>
    </div>
  );
}

export function BudgetScreen({ scope, go }: { scope: Scope; go: (route: RouteKey) => void }) {
  const { workspace, loading, error, service, run } = usePlannerData();
  const [tab, setTab] = React.useState<BudgetTab>("overview");
  const [adding, setAdding] = React.useState(false);
  const [addingAttempt, setAddingAttempt] = React.useState(0);
  const [addingCategory, setAddingCategory] = React.useState(false);
  const [addingCategoryAttempt, setAddingCategoryAttempt] = React.useState(0);
  const [editingCategory, setEditingCategory] = React.useState<BudgetCategory | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const { submitting: savingExpense, guardedSubmit: guardedSaveExpense } = useSubmitGuard();
  const { submitting: savingCategory, guardedSubmit: guardedSaveCategory } = useSubmitGuard();
  const expenseIdempotencyKey = useIdempotencyKey(addingAttempt);
  const categoryIdempotencyKey = useIdempotencyKey(addingCategoryAttempt);
  if (loading || !workspace) return <div className="pob-pad"><LoadingState label="Loading budget" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;

  const categories = workspace.budgetCategories;
  const scopedExpenses = workspace.budgetExpenses.filter((expense) => scopedRecord(expense, scope));
  const scopedPayments = workspace.payments.filter((payment) => scopedRecord(payment, scope));
  const totalBudget = categories.reduce((sum, category) => sum + category.allocation, 0);
  const committed = scope === "all" ? categories.reduce((sum, category) => sum + category.committed, 0) : scopedExpenses.reduce((sum, expense) => sum + expense.amount, 0);
  const paid = scope === "all" ? categories.reduce((sum, category) => sum + category.paid, 0) : scopedExpenses.reduce((sum, expense) => sum + expense.paidAmount, 0);
  const over = categories.filter((category) => category.committed > category.allocation);
  const dueSoon = scopedPayments.filter((payment) => payment.status === "due" || payment.status === "upcoming");

  return (
    <div>
      <PageHeader
        eyebrow={scope === "all" ? "Money" : eventName(workspace, scope)}
        title="Budget"
        meta={[
          { icon: "wallet", label: `${money(totalBudget, workspace.wedding.currency)} budget` },
          { icon: "receipt", label: `${money(paid, workspace.wedding.currency)} paid` },
          { icon: "coins", label: `${workspace.wedding.currency} reporting currency` }
        ]}
        actions={categories.length === 0
          ? <Button variant="primary" icon="plus" onClick={() => { setAddingCategoryAttempt((n) => n + 1); setAddingCategory(true); }}>Set total budget</Button>
          : <><DisabledReason reason="Budget export is waiting for the reporting/export API."><Button icon="download" disabled>Export</Button></DisabledReason><Button icon="users" onClick={() => setTab("contributor")}>Contributions</Button><Button icon="plus" onClick={() => { setAddingCategoryAttempt((n) => n + 1); setAddingCategory(true); }}>Add category</Button><Button variant="primary" icon="plus" onClick={() => { setAddingAttempt((n) => n + 1); setAdding(true); }}>Add expense</Button></>}
        tabs={<Tabs value={tab} onChange={setTab} tabs={[
          { value: "overview", label: "Overview" },
          { value: "category", label: "By category" },
          { value: "event", label: "By celebration" },
          { value: "contributor", label: "By contributor" }
        ]} />}
      />
      <div className="pob-pad">
        {actionError && <Callout tone="warning" title="Budget action not saved">{actionError}</Callout>}
        {categories.length === 0 ? (
          <EmptyState
            icon="wallet"
            title="No budget set up yet"
            description="Add your first budget category (for example, Venue or Catering) and how much you're allocating to it. That becomes your total budget, and you can then start adding expenses against it."
            action={<Button variant="primary" icon="plus" onClick={() => { setAddingCategoryAttempt((n) => n + 1); setAddingCategory(true); }}>Add a budget category</Button>}
          />
        ) : (
          <>
            {tab === "overview" && (
              <>
                <div className="pob-grid-4">
                  <Card padding="var(--space-5)"><Stat label="Total budget" value={shortMoney(totalBudget)} caption={money(totalBudget, workspace.wedding.currency)} /></Card>
                  <Card padding="var(--space-5)"><Stat label="Committed" value={shortMoney(committed)} caption={`${Math.round((committed / Math.max(totalBudget, 1)) * 100)}% of budget`} /></Card>
                  <Card padding="var(--space-5)"><Stat label="Paid to date" value={shortMoney(paid)} caption={`${Math.round((paid / Math.max(committed, 1)) * 100)}% of committed`} /></Card>
                  <Card padding="var(--space-5)"><Stat label="Available" value={shortMoney(totalBudget - committed)} caption="unallocated" /></Card>
                </div>
                <Card eyebrow="Allocation" title="Where the money is going">
                  <div style={{ display: "flex", height: 12, borderRadius: "var(--radius-pill)", overflow: "hidden", background: "var(--ivory-300)" }}>
                    {categories.map((category) => <span key={category.id} style={{ width: `${Math.max(2, (category.committed / Math.max(committed, 1)) * 100)}%`, background: category.color }} />)}
                  </div>
                  <div style={{ display: "flex", gap: "var(--space-5)", flexWrap: "wrap", marginTop: "var(--space-4)" }}>
                    {categories.map((category) => <Legend key={category.id} color={category.color} label={category.name} value={shortMoney(category.committed)} />)}
                  </div>
                </Card>
                {over.map((category) => (
                  <Callout key={category.id} tone="error" title={`${category.name} is ${money(category.committed - category.allocation, workspace.wedding.currency)} over allocation`} action={<Button size="sm" onClick={() => go("payments")}>Review expense</Button>}>
                    {category.why || "Review committed expenses in this category."}
                  </Callout>
                ))}
                {dueSoon.length > 0 && (
                  <Callout tone="error" title={`${dueSoon.length} payments totalling ${money(dueSoon.reduce((sum, item) => sum + item.amount, 0), workspace.wedding.currency)} fall due soon`} action={<Button size="sm" onClick={() => go("payments")}>Payment schedule</Button>}>
                    {dueSoon.map((payment) => payment.vendorName).join(", ")}.
                  </Callout>
                )}
              </>
            )}
            {(tab === "overview" || tab === "category") && <BudgetCategoryTable categories={categories} currency={workspace.wedding.currency} onEdit={setEditingCategory} />}
          </>
        )}
        {tab === "event" && <BudgetEventTable scope={scope} />}
        {tab === "contributor" && <ContributorTable />}
      </div>
      {adding && <AddExpenseDialog submitting={savingExpense} onClose={() => setAdding(false)} onSubmit={(input) => guardedSaveExpense(async () => {
        setActionError(null);
        try {
          await run(() => service.budget.createExpense(input, expenseIdempotencyKey));
          setAdding(false);
        } catch (err) {
          setActionError(errorMessage(err));
        }
      })} />}
      {addingCategory && <AddCategoryDialog submitting={savingCategory} onClose={() => setAddingCategory(false)} onSubmit={(input) => guardedSaveCategory(async () => {
        setActionError(null);
        try {
          await run(() => service.budget.createCategory(input, categoryIdempotencyKey));
          setAddingCategory(false);
        } catch (err) {
          setActionError(errorMessage(err));
        }
      })} />}
      {editingCategory && <AddCategoryDialog category={editingCategory} submitting={savingCategory} onClose={() => setEditingCategory(null)} onSubmit={(input) => guardedSaveCategory(async () => {
        setActionError(null);
        try {
          await run(() => service.budget.updateCategory(editingCategory.id, input));
          setEditingCategory(null);
        } catch (err) {
          setActionError(errorMessage(err));
        }
      })} />}
    </div>
  );
}

function BudgetCategoryTable({ categories, currency, onEdit }: { categories: BudgetCategory[]; currency: string; onEdit: (category: BudgetCategory) => void }) {
  return (
    <Card eyebrow="By category" title="Allocation and spend" padding="0">
      <DataTable rows={categories} emptyLabel="No budget categories yet" columns={[
        { key: "name", header: "Category", render: (category) => <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: category.color }} /><span style={{ font: "var(--type-label)" }}>{category.name}</span></span> },
        { key: "bar", header: "Committed", width: 160, render: (category) => <ProgressBar value={category.committed} max={category.allocation || 1} height={5} /> },
        { key: "allocation", header: "Allocation", align: "right", render: (category) => money(category.allocation, currency) },
        { key: "committed", header: "Committed", align: "right", render: (category) => money(category.committed, currency) },
        { key: "paid", header: "Paid", align: "right", muted: true, render: (category) => money(category.paid, currency) },
        { key: "balance", header: "Balance", align: "right", render: (category) => money(category.committed - category.paid, currency) },
        { key: "state", header: "State", render: (category) => category.committed > category.allocation ? <Badge tone="error">Over</Badge> : category.committed === category.allocation ? <Badge tone="neutral">Allocated</Badge> : <Badge tone="success">Within</Badge> },
        { key: "actions", header: "", width: 90, render: (category) => <Button size="sm" onClick={() => onEdit(category)}>Edit</Button> }
      ]} />
    </Card>
  );
}

function AddCategoryDialog({ category, onClose, onSubmit, submitting }: { category?: BudgetCategory; onClose: () => void; onSubmit: (input: { name: string; allocation?: number }) => void; submitting?: boolean }) {
  const [name, setName] = React.useState(category?.name || "");
  const [allocation, setAllocation] = React.useState(category?.allocation ?? 0);
  const valid = Boolean(name.trim());
  return (
    <Dialog
      title={category ? `Edit ${category.name}` : "Add budget category"}
      description={category ? "Rename this category or change how much of the budget it gets." : "A category (Venue, Catering, Decor...) with how much you're allocating to it. Together, your categories are your total budget."}
      onClose={onClose}
      footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" disabled={!valid || submitting} onClick={() => onSubmit({ name, allocation })}>{submitting ? "Saving…" : category ? "Save" : "Add category"}</Button></>}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <Field label="Category name"><Input value={name} onChange={(event) => setName(event.target.value)} /></Field>
        <Field label="Allocation (optional)"><Input type="number" value={allocation || ""} onChange={(event) => setAllocation(Number(event.target.value))} /></Field>
      </div>
    </Dialog>
  );
}

function BudgetEventTable({ scope }: { scope: Scope }) {
  const { workspace } = usePlannerData();
  if (!workspace) return null;
  const ids = scope === "all" ? [...workspace.events.map((event) => event.id), "shared"] : [scope];
  const rows = ids.map((eventId) => {
    const expenses = workspace.budgetExpenses.filter((expense) => eventId === "shared" ? expense.eventId === "shared" || !expense.eventId : expense.eventId === eventId);
    return {
      id: eventId,
      name: eventId === "shared" ? "Shared costs" : eventName(workspace, eventId),
      committed: expenses.reduce((sum, expense) => sum + expense.amount, 0),
      paid: expenses.reduce((sum, expense) => sum + expense.paidAmount, 0),
      rows: expenses.length
    };
  });
  return (
    <>
      <Card eyebrow="By celebration" title="Direct and shared records" padding="0" action={<DisabledReason reason="Apportionment changes require the backend money reporting API."><Button variant="ghost" size="sm" disabled>Change apportionment</Button></DisabledReason>}>
        <DataTable rows={rows} emptyLabel="No expenses in this scope" columns={[
          { key: "name", header: "Celebration", render: (row) => <span style={{ font: "var(--type-label)" }}>{row.name}</span> },
          { key: "rows", header: "Rows", align: "right" },
          { key: "committed", header: "Committed", align: "right", render: (row) => money(row.committed, workspace.wedding.currency) },
          { key: "paid", header: "Paid", align: "right", muted: true, render: (row) => money(row.paid, workspace.wedding.currency) },
          { key: "balance", header: "Balance", align: "right", render: (row) => money(row.committed - row.paid, workspace.wedding.currency) }
        ]} />
      </Card>
      <Callout tone="neutral" title="Shared costs remain single records">
        Shared expenses are reported across celebrations without duplicating the underlying expense row.
      </Callout>
    </>
  );
}

function ContributorTable() {
  const { workspace } = usePlannerData();
  if (!workspace) return null;
  const rows = Array.from(workspace.budgetExpenses.reduce((map, expense) => {
    const current = map.get(expense.payer) || { id: expense.payer, who: expense.payer, committed: 0, paid: 0 };
    current.committed += expense.amount;
    current.paid += expense.paidAmount;
    map.set(expense.payer, current);
    return map;
  }, new Map<string, { id: string; who: string; committed: number; paid: number }>()).values());
  return (
    <>
      <Card eyebrow="By contributor" title="Who is paying" padding="0">
        <DataTable rows={rows} emptyLabel="No contributor assignments yet" columns={[
          { key: "who", header: "Contributor", render: (row) => <span style={{ font: "var(--type-label)" }}>{row.who}</span> },
          { key: "committed", header: "Committed", align: "right", render: (row) => money(row.committed, workspace.wedding.currency) },
          { key: "paid", header: "Paid", align: "right", render: (row) => money(row.paid, workspace.wedding.currency) },
          { key: "balance", header: "Outstanding", align: "right", render: (row) => money(row.committed - row.paid, workspace.wedding.currency) },
          { key: "bar", header: "", width: 150, render: (row) => <ProgressBar value={row.paid} max={row.committed || 1} height={5} /> }
        ]} />
      </Card>
      <Callout tone="neutral" title="Contributors are explicit">
        A payer is assigned on each expense and is not inferred from the celebration.
      </Callout>
    </>
  );
}

function Legend({ color, label, value }: { color: string; label: string; value: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7, font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: color }} />{label}
      <span className="pob-tnum" style={{ color: "var(--text-primary)" }}>{value}</span>
    </span>
  );
}

function AddExpenseDialog({ onClose, onSubmit, submitting }: { onClose: () => void; onSubmit: (input: BudgetExpenseInput) => void; submitting?: boolean }) {
  const { workspace } = usePlannerData();
  const [draft, setDraft] = React.useState<BudgetExpenseInput>({ categoryId: "", vendorId: "", eventId: "shared", item: "", amount: 0, payer: "Couple", dueDate: "" });
  if (!workspace) return null;
  const categories = workspace.budgetCategories;
  const categoryId = draft.categoryId || categories[0]?.id || "";
  const valid = Boolean(categoryId && draft.item.trim() && Number(draft.amount) > 0 && draft.payer.trim());
  const eventOptions = [{ value: "shared", label: "Shared costs" }, ...workspace.events.map((event) => ({ value: event.id, label: event.name }))];
  return (
    <Dialog title="Add expense" description="Adds a committed budget row and, when due date is present, a scheduled payment." onClose={onClose} footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" disabled={!valid || submitting} onClick={() => onSubmit({ ...draft, categoryId, vendorId: draft.vendorId || undefined, dueDate: draft.dueDate || undefined })}>{submitting ? "Adding…" : "Add expense"}</Button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <Field label="Expense"><Input value={draft.item} onChange={(event) => setDraft({ ...draft, item: event.target.value })} /></Field>
        <div className="pob-grid-2">
          <Field label="Category"><Select value={categoryId} onChange={(event) => setDraft({ ...draft, categoryId: event.target.value })} options={categories.map((category) => ({ value: category.id, label: category.name }))} /></Field>
          <Field label="Amount"><Input type="number" value={draft.amount || ""} onChange={(event) => setDraft({ ...draft, amount: Number(event.target.value) })} /></Field>
          <Field label="Vendor"><Select value={draft.vendorId || ""} onChange={(event) => setDraft({ ...draft, vendorId: event.target.value || undefined })} options={[{ value: "", label: "No vendor" }, ...workspace.vendors.map((vendor) => ({ value: vendor.id, label: vendor.name }))]} /></Field>
          <Field label="Celebration"><Select value={draft.eventId || "shared"} onChange={(event) => setDraft({ ...draft, eventId: event.target.value as ID | "shared" })} options={eventOptions} /></Field>
          <Field label="Paying"><Select value={draft.payer} onChange={(event) => setDraft({ ...draft, payer: event.target.value })} options={["Couple", "Bride's family", "Groom's family", "Other"]} /></Field>
          <Field label="Due"><Input type="date" value={draft.dueDate || ""} onChange={(event) => setDraft({ ...draft, dueDate: event.target.value })} /></Field>
        </div>
      </div>
    </Dialog>
  );
}

export function PaymentsScreen({ scope }: { scope: Scope; go: (route: RouteKey) => void }) {
  const { workspace, loading, error, service, run } = usePlannerData();
  const [recording, setRecording] = React.useState<Payment | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const { submitting: recordingPayment, guardedSubmit: guardedRecordPayment } = useSubmitGuard();
  if (loading || !workspace) return <div className="pob-pad"><LoadingState label="Loading payments" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;

  const rows = workspace.payments.filter((payment) => scopedRecord(payment, scope));
  const outstanding = rows.filter((payment) => payment.status !== "paid").reduce((sum, payment) => sum + payment.amount, 0);
  const dueSoon = rows.filter((payment) => payment.status === "due" || payment.status === "upcoming");
  const nextDue = dueSoon[0]?.dueDate ? formatShortDate(dueSoon[0].dueDate) : "None";
  return (
    <div>
      <PageHeader
        eyebrow={scope === "all" ? "Money" : eventName(workspace, scope)}
        title="Payments"
        meta={[
          { icon: "receipt", label: `${rows.length} scheduled` },
          { icon: "wallet", label: `${money(outstanding, workspace.wedding.currency)} outstanding` },
          { icon: "calendar-clock", label: `Next due ${nextDue}` }
        ]}
        actions={<><DisabledReason reason="Payment export is waiting for the reporting/export API."><Button icon="download" disabled>Export</Button></DisabledReason><Button variant="primary" icon="plus" disabled={!rows.some((payment) => payment.status !== "paid")} onClick={() => setRecording(rows.find((payment) => payment.status !== "paid") || null)}>Record payment</Button></>}
      />
      <div className="pob-pad">
        {actionError && <Callout tone="warning" title="Payment not recorded">{actionError}</Callout>}
        {dueSoon.length > 0 && (
          <Callout tone="error" title={`${dueSoon.length} payments totalling ${money(dueSoon.reduce((sum, item) => sum + item.amount, 0), workspace.wedding.currency)} due soon`} action={<DisabledReason reason="Payment reminders are waiting for the messaging API."><Button size="sm" icon="bell" disabled>Remind me</Button></DisabledReason>}>
            {dueSoon.map((payment) => payment.vendorName).join(", ")}.
          </Callout>
        )}
        <Card eyebrow="This week" title="Due now" padding="0">
          <DataTable rows={dueSoon} emptyLabel="Nothing due this week" columns={[
            { key: "vendorName", header: "Vendor", render: (payment) => <span style={{ font: "var(--type-label)" }}>{payment.vendorName}</span> },
            { key: "item", header: "Expense", muted: true },
            { key: "event", header: "Celebration", muted: true, render: (payment) => payment.eventId === "shared" || !payment.eventId ? "Shared" : eventName(workspace, payment.eventId) },
            { key: "amount", header: "Amount", align: "right", render: (payment) => money(payment.amount, workspace.wedding.currency) },
            { key: "dueDate", header: "Due", render: (payment) => formatShortDate(payment.dueDate) },
            { key: "status", header: "Status", render: (payment) => <Badge dot tone={paymentTone[payment.status]}>{paymentLabels[payment.status]}</Badge> },
            { key: "payer", header: "Paying", muted: true },
            { key: "actions", header: "", width: 120, render: (payment) => payment.status === "paid" ? <span style={{ font: "var(--type-body-sm)", color: "var(--text-tertiary)" }}>Receipt attached</span> : <Button size="sm" variant="primary" onClick={() => setRecording(payment)}>Mark paid</Button> }
          ]} />
        </Card>
        <Card eyebrow="Later" title="Scheduled" padding="0">
          <DataTable dense rows={rows.filter((payment) => !dueSoon.some((item) => item.id === payment.id))} emptyLabel="No later payments scheduled" columns={[
            { key: "vendorName", header: "Vendor" },
            { key: "item", header: "Expense", muted: true },
            { key: "event", header: "Celebration", muted: true, render: (payment) => payment.eventId === "shared" || !payment.eventId ? "Shared" : eventName(workspace, payment.eventId) },
            { key: "amount", header: "Amount", align: "right", render: (payment) => money(payment.amount, workspace.wedding.currency) },
            { key: "dueDate", header: "Due", render: (payment) => formatShortDate(payment.dueDate) },
            { key: "status", header: "Status", render: (payment) => <Badge dot tone={paymentTone[payment.status]}>{paymentLabels[payment.status]}</Badge> },
            { key: "payer", header: "Paying", muted: true },
            { key: "actions", header: "", width: 120, render: (payment) => payment.status === "paid" ? <Badge tone="success">Paid</Badge> : <Button size="sm" onClick={() => setRecording(payment)}>Mark paid</Button> }
          ]} />
        </Card>
      </div>
      {recording && <RecordPaymentDialog payment={recording} submitting={recordingPayment} onClose={() => setRecording(null)} onSubmit={(payment, input, receiptFile) => guardedRecordPayment(async () => {
        setActionError(null);
        try {
          // Product Excellence Pass 1 §5: a receipt is a real file uploaded through the same
          // upload-url -> PUT bytes -> confirm pipeline Documents itself uses, never a typed
          // filename — see service.documents.uploadDocument.
          let receiptDocumentId: ID | undefined;
          if (receiptFile) {
            const document = await service.documents.uploadDocument(receiptFile, { type: "Receipt", linkedRecordLabel: `Payment: ${payment.item}` });
            receiptDocumentId = document.id;
          }
          await run(() => service.payments.recordPayment(payment.id, { ...input, receiptDocumentId }));
          setRecording(null);
        } catch (err) {
          setActionError(errorMessage(err));
        }
      })} />}
    </div>
  );
}

const MAX_RECEIPT_SIZE_BYTES = 25 * 1024 * 1024;

function RecordPaymentDialog({ payment, onClose, onSubmit, submitting }: { payment: Payment; onClose: () => void; onSubmit: (payment: Payment, input: { amount: number; paidAt: string; method: string; payer: string }, receiptFile: File | null) => void; submitting?: boolean }) {
  const [draft, setDraft] = React.useState({ amount: payment.amount, paidAt: new Date().toISOString().slice(0, 10), method: "Bank transfer", payer: payment.payer });
  const [receiptFile, setReceiptFile] = React.useState<File | null>(null);
  const [fileError, setFileError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const valid = draft.amount > 0 && draft.paidAt && draft.method && draft.payer;

  const handleFile = (files: FileList | null) => {
    const selected = files?.[0];
    if (!selected) return;
    if (selected.size > MAX_RECEIPT_SIZE_BYTES) {
      setFileError("That file is larger than the 25 MB limit.");
      setReceiptFile(null);
      return;
    }
    setFileError(null);
    setReceiptFile(selected);
  };

  return (
    <Dialog title={`Record payment to ${payment.vendorName}`} description={`${money(payment.amount)} · ${payment.item}`} onClose={onClose} footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" disabled={!valid || submitting} onClick={() => onSubmit(payment, draft, receiptFile)}>{submitting ? "Saving…" : "Save payment"}</Button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <div className="pob-grid-2">
          <Field label="Amount"><Input type="number" value={draft.amount} onChange={(event) => setDraft({ ...draft, amount: Number(event.target.value) })} /></Field>
          <Field label="Date"><Input type="date" value={draft.paidAt} onChange={(event) => setDraft({ ...draft, paidAt: event.target.value })} /></Field>
          <Field label="Method"><Select value={draft.method} onChange={(event) => setDraft({ ...draft, method: event.target.value })} options={["Bank transfer", "Card", "Cash", "Cheque"]} /></Field>
          <Field label="Paid by"><Select value={draft.payer} onChange={(event) => setDraft({ ...draft, payer: event.target.value })} options={["Couple", "Bride's family", "Groom's family", "Other"]} /></Field>
        </div>
        {fileError && <Callout tone="error" title="Can't use this file">{fileError}</Callout>}
        {/* Product Excellence Pass 1 §5: a real file, uploaded for real on save -- not a typed
            filename that only ever created empty Document metadata. */}
        <Field label="Receipt" hint="Optional -- PDF or an image, up to 25 MB">
          <div onClick={() => inputRef.current?.click()} style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", border: "1px dashed var(--border-subtle)", borderRadius: "var(--radius-md)", padding: "var(--space-3)", cursor: "pointer" }}>
            <input ref={inputRef} type="file" accept="application/pdf,image/*" style={{ display: "none" }} onChange={(event) => handleFile(event.target.files)} />
            <span style={{ font: "var(--type-body-sm)" }}>{receiptFile ? receiptFile.name : "Click to attach a receipt"}</span>
          </div>
        </Field>
      </div>
    </Dialog>
  );
}

export function VendorsScreen({ scope, go }: { scope: Scope; go: (route: RouteKey) => void }) {
  const { workspace, loading, error, service, run } = usePlannerData();
  const [tab, setTab] = React.useState<VendorTab>("all");
  const [drawer, setDrawer] = React.useState<Vendor | null>(null);
  const [adding, setAdding] = React.useState(false);
  const [addingAttempt, setAddingAttempt] = React.useState(0);
  const [comparing, setComparing] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const { submitting: savingVendor, guardedSubmit: guardedSaveVendor } = useSubmitGuard();
  const vendorIdempotencyKey = useIdempotencyKey(addingAttempt);
  if (loading || !workspace) return <div className="pob-pad"><LoadingState label="Loading vendors" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;

  const all = workspace.vendors.filter((vendor) => scopedMultiRecord(vendor, scope));
  const rows = all.filter((vendor) => tab === "all" || vendor.status === tab);
  return (
    <div>
      <PageHeader
        eyebrow={scope === "all" ? "Vendors" : eventName(workspace, scope)}
        title="Vendors"
        meta={[
          { icon: "store", label: `${rows.length} shown` },
          { icon: "check", label: `${all.filter((vendor) => vendor.status === "booked").length} booked` },
          { icon: "wallet", label: `${money(all.reduce((sum, vendor) => sum + vendor.committedAmount, 0), workspace.wedding.currency)} committed` }
        ]}
        actions={<><Button icon="columns-3" onClick={() => setComparing(true)}>Compare shortlist</Button><Button variant="primary" icon="plus" onClick={() => { setAddingAttempt((n) => n + 1); setAdding(true); }}>Add vendor</Button></>}
        tabs={<Tabs value={tab} onChange={setTab} tabs={[
          { value: "all", label: "All", count: all.length },
          { value: "booked", label: "Booked", count: all.filter((vendor) => vendor.status === "booked").length },
          { value: "quoted", label: "Quoted", count: all.filter((vendor) => vendor.status === "quoted").length },
          { value: "shortlisted", label: "Shortlisted", count: all.filter((vendor) => vendor.status === "shortlisted").length },
          { value: "contacted", label: "Contacted", count: all.filter((vendor) => vendor.status === "contacted").length },
          { value: "rejected", label: "Rejected", count: all.filter((vendor) => vendor.status === "rejected").length }
        ]} />}
      />
      <div className="pob-pad">
        {actionError && <Callout tone="warning" title="Vendor action not saved">{actionError}</Callout>}
        <Card padding="0">
          <DataTable rows={rows} onRowClick={setDrawer} emptyLabel="No vendors in this view" columns={[
            { key: "name", header: "Vendor", render: (vendor) => <span style={{ font: "var(--type-label)" }}>{vendor.name}</span> },
            { key: "category", header: "Category", muted: true },
            { key: "events", header: "Celebrations", muted: true, render: (vendor) => vendor.eventIds.length ? vendor.eventIds.map((eventId) => eventId === "shared" ? "Shared" : eventName(workspace, eventId)).join(", ") : "-" },
            { key: "contactName", header: "Contact", muted: true },
            { key: "status", header: "Status", render: (vendor) => <Badge dot tone={vendorTone[vendor.status]}>{vendorLabels[vendor.status]}</Badge> },
            { key: "committedAmount", header: "Committed", align: "right", render: (vendor) => vendor.committedAmount ? money(vendor.committedAmount, workspace.wedding.currency) : "-" },
            { key: "nextStep", header: "Next step", muted: true, render: (vendor) => vendor.nextStep || "-" }
          ]} />
        </Card>
        <Callout tone="neutral" title="One record, many celebrations">
          Vendor records can serve one celebration, multiple celebrations, or shared whole-wedding work without duplication.
        </Callout>
      </div>
      {adding && <VendorDialog submitting={savingVendor} onClose={() => setAdding(false)} onSubmit={(input) => guardedSaveVendor(async () => {
        setActionError(null);
        try {
          await run(() => service.vendors.createVendor(input, vendorIdempotencyKey));
          setAdding(false);
        } catch (err) {
          setActionError(errorMessage(err));
        }
      })} />}
      {drawer && <VendorDrawer vendor={drawer} onClose={() => setDrawer(null)} go={go} />}
      {comparing && <CompareDialog onClose={() => setComparing(false)} onError={setActionError} />}
    </div>
  );
}

function VendorDialog({ vendor, onClose, onSubmit, submitting }: { vendor?: Vendor; onClose: () => void; onSubmit: (input: VendorInput) => void; submitting?: boolean }) {
  const { workspace } = usePlannerData();
  const [draft, setDraft] = React.useState<VendorInput>(vendor ? {
    name: vendor.name,
    category: vendor.category,
    eventIds: vendor.eventIds,
    contactName: vendor.contactName,
    phone: vendor.phone || "",
    email: vendor.email || "",
    status: vendor.status,
    committedAmount: vendor.committedAmount,
    nextStep: vendor.nextStep || "",
    notes: vendor.notes || ""
  } : { name: "", category: "Photography", eventIds: [], contactName: "", status: "contacted", committedAmount: 0, nextStep: "" });
  if (!workspace) return null;
  const valid = Boolean(draft.name.trim() && draft.category.trim() && draft.contactName.trim());
  const toggleEvent = (eventId: ID | "shared") => setDraft({ ...draft, eventIds: draft.eventIds.includes(eventId) ? draft.eventIds.filter((item) => item !== eventId) : [...draft.eventIds, eventId] });
  return (
    <Dialog title={vendor ? `Edit ${vendor.name}` : "Add vendor"} description="Vendor records stay single even when they serve multiple celebrations." onClose={onClose} footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" disabled={!valid || submitting} onClick={() => onSubmit(draft)}>{submitting ? "Saving…" : vendor ? "Save" : "Add vendor"}</Button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <Field label="Name"><Input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></Field>
        <div className="pob-grid-2">
          <Field label="Category"><Input value={draft.category} onChange={(event) => setDraft({ ...draft, category: event.target.value })} /></Field>
          <Field label="Status"><Select value={draft.status || "contacted"} onChange={(event) => setDraft({ ...draft, status: event.target.value as VendorStatus })} options={Object.entries(vendorLabels).map(([value, label]) => ({ value, label }))} /></Field>
          <Field label="Contact"><Input value={draft.contactName} onChange={(event) => setDraft({ ...draft, contactName: event.target.value })} /></Field>
          <Field label="Committed"><Input type="number" value={draft.committedAmount || ""} onChange={(event) => setDraft({ ...draft, committedAmount: Number(event.target.value) })} /></Field>
          <Field label="Phone"><Input value={draft.phone || ""} onChange={(event) => setDraft({ ...draft, phone: event.target.value })} /></Field>
          <Field label="Email"><Input value={draft.email || ""} onChange={(event) => setDraft({ ...draft, email: event.target.value })} /></Field>
        </div>
        <Field label="Celebrations">
          <div className="pob-grid-2">
            <Checkbox checked={draft.eventIds.includes("shared")} label="Shared" onChange={() => toggleEvent("shared")} />
            {workspace.events.map((event) => <Checkbox key={event.id} checked={draft.eventIds.includes(event.id)} label={event.name} description={formatShortDate(event.date)} onChange={() => toggleEvent(event.id)} />)}
          </div>
        </Field>
        <Field label="Next step"><Input value={draft.nextStep || ""} onChange={(event) => setDraft({ ...draft, nextStep: event.target.value })} /></Field>
        <Field label="Notes"><Textarea value={draft.notes || ""} onChange={(event) => setDraft({ ...draft, notes: event.target.value })} /></Field>
      </div>
    </Dialog>
  );
}

function VendorDrawer({ vendor, onClose, go }: { vendor: Vendor; onClose: () => void; go: (route: RouteKey) => void }) {
  const { workspace, service, run } = usePlannerData();
  const [tab, setTab] = React.useState<"Overview" | "Agreement" | "Payments" | "Tasks" | "Documents" | "Notes">("Overview");
  const [editing, setEditing] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const { submitting: togglingShortlist, guardedSubmit: guardedToggleShortlist } = useSubmitGuard();
  const { submitting: savingVendorEdit, guardedSubmit: guardedSaveVendorEdit } = useSubmitGuard();
  if (!workspace) return null;
  const current = workspace.vendors.find((item) => item.id === vendor.id) || vendor;
  const isShortlisted = current.status === "shortlisted";
  const toggleShortlist = () => guardedToggleShortlist(async () => {
    setActionError(null);
    try {
      await run(() => service.vendors.updateVendor(current.id, { status: isShortlisted ? "contacted" : "shortlisted" }));
    } catch (err) {
      setActionError(errorMessage(err));
    }
  });
  const payments = workspace.payments.filter((payment) => payment.vendorId === current.id || payment.vendorName === current.name);
  const tasks = workspace.tasks.filter((task) => task.vendorId === current.id || task.vendorName === current.name);
  const documents = workspace.documents.filter((document) => document.linkedRecordLabel === current.name);
  return (
    <Drawer title={current.name} subtitle={`${current.category} · ${vendorLabels[current.status]}`} onClose={onClose}>
      {actionError && <Callout tone="warning" title="Vendor action not saved">{actionError}</Callout>}
      <Tabs value={tab} onChange={setTab} tabs={["Overview", "Agreement", "Payments", "Tasks", "Documents", "Notes"].map((label) => ({ value: label as typeof tab, label }))} />
      <div className="pob-grid-4">
        <Card tone="sunken" padding="var(--space-4)"><Stat label="Committed" value={shortMoney(current.committedAmount)} /></Card>
        <Card tone="sunken" padding="var(--space-4)"><Stat label="Paid" value={money(current.paidAmount, workspace.wedding.currency)} /></Card>
        <Card tone="sunken" padding="var(--space-4)"><Stat label="Balance" value={money(current.committedAmount - current.paidAmount, workspace.wedding.currency)} /></Card>
        <Card tone="sunken" padding="var(--space-4)"><Stat label="Records" value={payments.length + tasks.length + documents.length} /></Card>
      </div>
      {tab === "Overview" && (
        <>
          <Callout tone="neutral" title={current.nextStep || "No next step recorded"}>{current.notes || `${current.contactName}${current.phone ? ` · ${current.phone}` : ""}`}</Callout>
          <RelatedPayments payments={payments} currency={workspace.wedding.currency} />
        </>
      )}
      {tab === "Agreement" && <TermsTable vendor={current} />}
      {tab === "Payments" && <RelatedPayments payments={payments} currency={workspace.wedding.currency} />}
      {tab === "Tasks" && (
        <Card padding="0">
          <DataTable dense rows={tasks} emptyLabel="No tasks joined to this vendor" columns={[
            { key: "title", header: "Task" },
            { key: "dueDate", header: "Due", muted: true, render: (task) => task.dueDate ? formatShortDate(task.dueDate) : "-" },
            { key: "status", header: "Status", render: (task) => <Badge dot tone={statusTone[task.status]}>{statusLabels[task.status]}</Badge> }
          ]} />
        </Card>
      )}
      {tab === "Documents" && (
        <Card padding="0">
          <DataTable dense rows={documents} emptyLabel="No documents linked to this vendor" columns={[
            { key: "name", header: "Document" },
            { key: "type", header: "Type", render: (document) => <Badge tone="neutral">{document.type}</Badge> },
            { key: "addedAt", header: "Added", muted: true }
          ]} />
        </Card>
      )}
      {tab === "Notes" && <Callout tone="neutral" title="Notes">{current.notes || "No notes recorded."}</Callout>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "var(--space-2)" }}>
        <Button onClick={onClose}>Close</Button>
        <DisabledReason reason="Timeline sharing is waiting for the messaging/export API."><Button icon="share-2" disabled>Share timeline</Button></DisabledReason>
        <Button icon="columns-3" disabled={togglingShortlist} onClick={toggleShortlist}>{togglingShortlist ? "Saving…" : isShortlisted ? "Remove from shortlist" : "Add to shortlist"}</Button>
        <Button onClick={() => setEditing(true)}>Edit vendor</Button>
        <Button variant="primary" onClick={() => { onClose(); go("payments"); }}>Record payment</Button>
      </div>
      {editing && <VendorDialog vendor={current} submitting={savingVendorEdit} onClose={() => setEditing(false)} onSubmit={(input) => guardedSaveVendorEdit(async () => {
        setActionError(null);
        try {
          await run(() => service.vendors.updateVendor(current.id, input));
          setEditing(false);
        } catch (err) {
          setActionError(errorMessage(err));
        }
      })} />}
    </Drawer>
  );
}

function TermsTable({ vendor }: { vendor: Vendor }) {
  return (
    <Card eyebrow="Agreement" title="Terms" padding="0">
      <DataTable dense rows={vendor.terms} emptyLabel="No agreement terms recorded" columns={[
        { key: "label", header: "", muted: true, width: 130 },
        { key: "value", header: "", wrap: true }
      ]} />
    </Card>
  );
}

function RelatedPayments({ payments, currency }: { payments: Payment[]; currency: string }) {
  return (
    <Card eyebrow="Payment schedule" padding="0">
      <DataTable dense rows={payments} emptyLabel="No payments scheduled" columns={[
        { key: "item", header: "Instalment" },
        { key: "amount", header: "Amount", align: "right", render: (payment) => money(payment.amount, currency) },
        { key: "dueDate", header: "Due", render: (payment) => formatShortDate(payment.dueDate) },
        { key: "status", header: "Status", render: (payment) => <Badge dot tone={paymentTone[payment.status]}>{paymentLabels[payment.status]}</Badge> }
      ]} />
    </Card>
  );
}

function CompareDialog({ onClose, onError }: { onClose: () => void; onError: (message: string | null) => void }) {
  const { workspace, service, run } = usePlannerData();
  if (!workspace) return null;
  const rows = workspace.vendors.filter((vendor) => vendor.status === "shortlisted" || vendor.status === "quoted").slice(0, 3);
  const fields = ["Quote", "Celebrations", "Next step", "Contact"];
  const book = async (vendor: Vendor) => {
    onError(null);
    try {
      await run(() => service.vendors.updateVendor(vendor.id, { status: "booked" }));
    } catch (err) {
      onError(errorMessage(err));
    }
  };
  return (
    <Dialog width={760} title="Shortlist comparison" description={rows.length ? `${rows.length} vendors in this comparison` : "No vendors are shortlisted yet"} onClose={onClose} footer={<><Button size="sm" onClick={onClose}>Close</Button><DisabledReason reason="Custom comparison fields require the vendors comparison API."><Button size="sm" disabled>Add comparison field</Button></DisabledReason></>}>
      {rows.length === 0 ? <EmptyState compact icon="columns-3" title="No shortlist yet" description="Open a vendor and choose Add to shortlist, or record a quote (set Status to Quoted), and it will appear here for comparison." action={<Button size="sm" onClick={onClose}>Go to vendors</Button>} /> : (
        <>
          <DataTable dense rows={fields.map((field) => ({
            id: field,
            field,
            a: fieldValue(rows[0], field, workspace),
            b: fieldValue(rows[1], field, workspace),
            c: fieldValue(rows[2], field, workspace)
          }))} columns={[
            { key: "field", header: "", render: (row) => <span style={{ font: "var(--type-label)" }}>{row.field}</span> },
            { key: "a", header: rows[0]?.name || "-" },
            { key: "b", header: rows[1]?.name || "-" },
            { key: "c", header: rows[2]?.name || "-" }
          ]} />
          <div style={{ display: "flex", gap: "var(--space-3)", marginTop: "var(--space-4)", flexWrap: "wrap" }}>
            {rows.map((vendor) => <Button key={vendor.id} size="sm" variant={vendor.status === "booked" ? "primary" : "secondary"} onClick={() => book(vendor)}>{vendor.status === "booked" ? "Booked" : `Book ${vendor.name}`}</Button>)}
          </div>
        </>
      )}
    </Dialog>
  );
}

function fieldValue(vendor: Vendor | undefined, field: string, workspace: NonNullable<ReturnType<typeof usePlannerData>["workspace"]>): string {
  if (!vendor) return "-";
  if (field === "Quote") return vendor.committedAmount ? money(vendor.committedAmount, workspace.wedding.currency) : "Not quoted";
  if (field === "Celebrations") return vendor.eventIds.length ? vendor.eventIds.map((eventId) => eventId === "shared" ? "Shared" : eventName(workspace, eventId)).join(", ") : "-";
  if (field === "Next step") return vendor.nextStep || "-";
  return `${vendor.contactName}${vendor.phone ? ` · ${vendor.phone}` : ""}`;
}

export function VenuesScreen({ scope }: { scope: Scope; go: (route: RouteKey) => void }) {
  const { workspace, loading, error, service, run } = usePlannerData();
  const [open, setOpen] = React.useState<Venue | null>(null);
  const [adding, setAdding] = React.useState(false);
  const [addingAttempt, setAddingAttempt] = React.useState(0);
  const [editing, setEditing] = React.useState<Venue | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const { submitting: savingVenue, guardedSubmit: guardedSaveVenue } = useSubmitGuard();
  const { submitting: savingVenueEdit, guardedSubmit: guardedSaveVenueEdit } = useSubmitGuard();
  const venueIdempotencyKey = useIdempotencyKey(addingAttempt);
  if (loading || !workspace) return <div className="pob-pad"><LoadingState label="Loading venues" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;
  const rows = workspace.venues.filter((venue) => scope === "all" || scope === "shared" || venue.eventIds.includes(scope));
  return (
    <div>
      <PageHeader
        eyebrow={scope === "all" ? "Vendors" : eventName(workspace, scope)}
        title="Venues"
        meta={[{ icon: "map-pin", label: `${rows.length} venues` }, { icon: "calendar-days", label: `${workspace.events.length} celebrations` }]}
        actions={<Button variant="primary" icon="plus" onClick={() => { setAddingAttempt((n) => n + 1); setAdding(true); }}>Add venue</Button>}
      />
      <div className="pob-pad">
        {actionError && <Callout tone="warning" title="Venue action not saved">{actionError}</Callout>}
        <Card padding="0">
          <DataTable rows={rows} onRowClick={setOpen} emptyLabel="No venues in this view" columns={[
            { key: "name", header: "Venue", render: (venue) => <span style={{ font: "var(--type-label)" }}>{venue.name}</span> },
            { key: "events", header: "Celebrations", muted: true, render: (venue) => venue.eventIds.map((eventId) => eventName(workspace, eventId)).join(", ") || "-" },
            { key: "city", header: "City", muted: true },
            { key: "capacity", header: "Capacity", align: "right" },
            { key: "access", header: "Access", muted: true },
            { key: "status", header: "Status", render: (venue) => <Badge dot tone={venue.status === "researching" ? "neutral" : "success"}>{venueLabels[venue.status]}</Badge> },
            { key: "x", header: "", width: 76, render: () => <Button size="sm">Open</Button> }
          ]} />
        </Card>
        <Callout tone="neutral" title="Capacity feeds downstream planning">
          Venue records are event-aware now, while floor-plan warnings remain a later module.
        </Callout>
      </div>
      {open && <VenueDetails venue={open} onClose={() => setOpen(null)} onEdit={() => setEditing(open)} />}
      {adding && <VenueDialog submitting={savingVenue} onClose={() => setAdding(false)} onSubmit={(input) => guardedSaveVenue(async () => {
        setActionError(null);
        try {
          await run(() => service.venues.createVenue(input, venueIdempotencyKey));
          setAdding(false);
        } catch (err) {
          setActionError(errorMessage(err));
        }
      })} />}
      {editing && <VenueDialog venue={editing} submitting={savingVenueEdit} onClose={() => setEditing(null)} onSubmit={(input) => guardedSaveVenueEdit(async () => {
        setActionError(null);
        try {
          await run(() => service.venues.updateVenue(editing.id, input));
          setEditing(null);
          setOpen(null);
        } catch (err) {
          setActionError(errorMessage(err));
        }
      })} />}
    </div>
  );
}

function VenueDetails({ venue, onClose, onEdit }: { venue: Venue; onClose: () => void; onEdit: () => void }) {
  const { workspace } = usePlannerData();
  if (!workspace) return null;
  return (
    <Dialog width={720} title={venue.name} description={`${venue.address} · capacity ${venue.capacity} seated`} onClose={onClose} footer={<><Button size="sm" onClick={onClose}>Close</Button><DisabledReason reason="Floor-plan editing belongs to the later seating/floor-plan module."><Button size="sm" disabled>Floor plan</Button></DisabledReason><Button size="sm" variant="primary" onClick={onEdit}>Edit venue</Button></>}>
      <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: "var(--space-4)" }}>
        <Card tone="sunken" padding="var(--space-4)">
          <span className="pob-eyebrow">Operating rules</span>
          <DataTable dense rows={venue.rules} emptyLabel="No venue rules recorded" columns={[
            { key: "label", header: "", muted: true, width: 128 },
            { key: "value", header: "", wrap: true }
          ]} />
        </Card>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
          <Card tone="sunken" padding="var(--space-4)">
            <span className="pob-eyebrow">Assigned to</span>
            <DataTable dense rows={venue.eventIds.map((eventId) => ({ id: eventId, event: eventName(workspace, eventId), value: formatShortDate(workspace.events.find((event) => event.id === eventId)?.date || "") }))} columns={[
              { key: "event", header: "" },
              { key: "value", header: "", align: "right" }
            ]} />
          </Card>
          <Card tone="sunken" padding="var(--space-4)">
            <span className="pob-eyebrow">Documents</span>
            <DataTable dense rows={venue.documents.map((document) => ({ id: document, document }))} emptyLabel="No documents linked" columns={[{ key: "document", header: "" }]} />
          </Card>
        </div>
      </div>
      {venue.note && <Callout tone="neutral" title="Why this matters elsewhere" style={{ marginTop: "var(--space-4)" }}>{venue.note}</Callout>}
    </Dialog>
  );
}

function VenueDialog({ venue, onClose, onSubmit, submitting }: { venue?: Venue; onClose: () => void; onSubmit: (input: VenueInput) => void; submitting?: boolean }) {
  const { workspace } = usePlannerData();
  const [draft, setDraft] = React.useState<VenueInput>(venue ? {
    name: venue.name,
    eventIds: venue.eventIds,
    city: venue.city,
    capacity: venue.capacity,
    access: venue.access,
    status: venue.status,
    address: venue.address,
    note: venue.note || ""
  } : { name: "", eventIds: [], city: "", capacity: 0, access: "", status: "researching", address: "", note: "" });
  if (!workspace) return null;
  const valid = Boolean(draft.name.trim() && draft.city.trim() && draft.address.trim() && draft.capacity > 0 && draft.eventIds.length > 0);
  const toggleEvent = (eventId: ID) => setDraft({ ...draft, eventIds: draft.eventIds.includes(eventId) ? draft.eventIds.filter((item) => item !== eventId) : [...draft.eventIds, eventId] });
  return (
    <Dialog title={venue ? `Edit ${venue.name}` : "Add venue"} description="Venues can be assigned to one or more celebrations." onClose={onClose} footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" disabled={!valid || submitting} onClick={() => onSubmit(draft)}>{submitting ? "Saving…" : venue ? "Save" : "Add venue"}</Button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <Field label="Name"><Input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></Field>
        <div className="pob-grid-2">
          <Field label="City"><Input value={draft.city} onChange={(event) => setDraft({ ...draft, city: event.target.value })} /></Field>
          <Field label="Capacity"><Input type="number" value={draft.capacity || ""} onChange={(event) => setDraft({ ...draft, capacity: Number(event.target.value) })} /></Field>
          <Field label="Access"><Input value={draft.access} onChange={(event) => setDraft({ ...draft, access: event.target.value })} /></Field>
          <Field label="Status"><Select value={draft.status || "researching"} onChange={(event) => setDraft({ ...draft, status: event.target.value as VenueStatus })} options={Object.entries(venueLabels).map(([value, label]) => ({ value, label }))} /></Field>
        </div>
        <Field label="Address"><Input value={draft.address} onChange={(event) => setDraft({ ...draft, address: event.target.value })} /></Field>
        <Field label="Celebrations">
          <div className="pob-grid-2">
            {workspace.events.map((event) => <Checkbox key={event.id} checked={draft.eventIds.includes(event.id)} label={event.name} description={formatShortDate(event.date)} onChange={() => toggleEvent(event.id)} />)}
          </div>
        </Field>
        <Field label="Note"><Textarea value={draft.note || ""} onChange={(event) => setDraft({ ...draft, note: event.target.value })} /></Field>
      </div>
    </Dialog>
  );
}

function Drawer({ title, subtitle, onClose, children }: { title: string; subtitle: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 45, background: "rgba(29,29,27,.18)" }} onClick={onClose}>
      <aside onClick={(event) => event.stopPropagation()} style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: 640, maxWidth: "100%", background: "var(--surface-page)", boxShadow: "var(--shadow-overlay)", overflowY: "auto", padding: "var(--space-6)", display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "var(--space-3)" }}>
          <div style={{ flex: 1 }}>
            <span className="pob-eyebrow">Record</span>
            <h2 style={{ font: "var(--type-section)", marginTop: 4 }}>{title}</h2>
            <p style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)", marginTop: 2 }}>{subtitle}</p>
          </div>
          <IconButton icon="x" label="Close" onClick={onClose} />
        </div>
        {children}
      </aside>
    </div>
  );
}
